/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type MouseEventHandler, type ReactNode } from 'react';
import { token } from '@atlaskit/tokens';
import { jsx, cssMap } from '@compiled/react';
import Tooltip from '@atlaskit/tooltip';
import { injectIntl, IntlProvider, useIntl, type WrappedComponentProps } from 'react-intl-next';
import messages from './messages';
import { useIsSwitcherTeam25TestCohort } from '../components/experiment-provider';

const styles = cssMap({
	// hover active style matching: packages/design-system/menu/src/internal/components/menu-item-primitive.tsx
	button: {
		border: 0,
		padding: 0,
		marginTop: 0,
		marginRight: 0,
		marginBottom: 0,
		marginLeft: token('space.025'),
		height: '48px',
		width: '48px',
		cursor: 'pointer',
		background: 'none',
		borderRadius: token('border.radius'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			color: 'currentColor',
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
			boxShadow: 'none',
			color: 'currentColor',
		},
	},
	// hover active style matching: packages/design-system/menu/src/internal/components/menu-item-primitive.tsx
	buttonRefreshed: {
		border: 0,
		padding: 0,
		marginTop: 0,
		marginRight: token('space.100'),
		marginBottom: 0,
		marginLeft: token('space.025'),
		height: '32px',
		width: '32px',
		cursor: 'pointer',
		background: 'none',
		borderRadius: token('border.radius'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			color: 'currentColor',
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
			boxShadow: 'none',
			color: 'currentColor',
		},
	},
});

interface Props {
	icon: ReactNode;
	onClick: MouseEventHandler<HTMLButtonElement>;
	expanded: boolean;
	productName: React.ReactNode;
	numSites: number;
}

const Toggle = ({ icon, onClick, expanded, productName, numSites }: Props) => {
	const intl = useIntl();
	const label = intl.formatMessage(messages.sites, {
		productName,
		numSites,
	}) as string;

	const isVisualRefreshEnabled = useIsSwitcherTeam25TestCohort();

	return (
		<Tooltip content={label} hideTooltipOnMouseDown position="top">
			<button
				css={[isVisualRefreshEnabled ? styles.buttonRefreshed : styles.button]}
				aria-label={label}
				aria-expanded={expanded}
				data-testid="switcher-expand-toggle"
				onClick={onClick}
			>
				{icon}
			</button>
		</Tooltip>
	);
};

const ItemDropdownToggle = ({
	icon,
	onClick,
	intl,
	expanded,
	productName,
	numSites,
}: Props & WrappedComponentProps) => {
	return intl ? (
		<Toggle
			icon={icon}
			onClick={onClick}
			expanded={expanded}
			productName={productName}
			numSites={numSites}
		/>
	) : (
		<IntlProvider locale="en">
			<Toggle
				icon={icon}
				onClick={onClick}
				expanded={expanded}
				productName={productName}
				numSites={numSites}
			/>
		</IntlProvider>
	);
};

export default injectIntl(ItemDropdownToggle, {
	enforceContext: false,
});
